import React, { forwardRef, type ReactElement } from 'react';
import { responsiveProp, ResponsiveProp } from '../utils/responsiveProp';
import { styled } from '../theming';
import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';

export type IconSize = 'xsmall' | 'small' | 'default' | 'large' | 'xlarge';
import { useTheme } from '../theming';
import { fallbackTheme } from '../theming/fallbackTheme';

const sizes = {
  xsmall: {
    height: tokens.size.x4,
    width: tokens.size.x4,
  },
  small: {
    height: tokens.size.x5,
    width: tokens.size.x5,
  },
  default: {
    height: tokens.size.x6,
    width: tokens.size.x6,
  },
  large: {
    height: tokens.size.x8,
    width: tokens.size.x8,
  },
  xlarge: {
    height: tokens.size.x10,
    width: tokens.size.x10,
  },
};

const StyledSVG = styled.svg<{ size: ResponsiveProp<IconSize> }>`
  ${({ size }) => ({
    ...responsiveProp(size, (size: IconSize) => ({
      height: sizes[size as keyof typeof sizes].height,
      width: sizes[size as keyof typeof sizes].width,
    })),
  })}
`;

export type InternalIconProps = {
  children?: ReactElement;
  /**
   * Determines the size of the icon
   * @default default
   */
  size?: ResponsiveProp<IconSize>;
  /**
   * Color variant of the icon
   */
  color?: string;
};

export function _Icon(
  { children, size = 'default', color, ...otherProps }: InternalIconProps,
  forwardedRef: React.Ref<SVGSVGElement>
) {
  const currentTheme = useTheme();
  const iconColor = color ? color : fallbackTheme(currentTheme).color.surface.foreground.primary;

  return (
    <StyledSVG
      viewBox="0 0 32 32"
      ref={forwardedRef}
      fill={iconColor}
      size={size}
      focusable={false}
      aria-hidden="true"
      {...otherProps}
    >
      {children}
    </StyledSVG>
  );
}

export const GenericIcon = forwardRef(_Icon);
